import type { Activity } from "@/mocks/leadActivityData";
import { activityIconColors, activityIcons } from "@/mocks/leadActivityData";

type Props = {
  activities: Activity[];
  onLogActivity?: () => void;
};

const extendedIconColors: Record<string, string> = {
  ...activityIconColors,
  WhatsApp: "bg-green-100 text-green-600",
  Meeting: "bg-blue-100 text-blue-600",
  Other: "bg-gray-100 text-gray-500",
  project_link: "bg-purple-100 text-purple-600",
};

const extendedIcons: Record<string, string> = {
  ...activityIcons,
  WhatsApp: "ri-whatsapp-line",
  Meeting: "ri-calendar-check-line",
  Other: "ri-more-line",
  project_link: "ri-building-line",
};

const typeBadgeColors: Record<string, string> = {
  Call: "bg-blue-50 text-blue-700",
  Note: "bg-amber-50 text-amber-700",
  Email: "bg-purple-50 text-purple-700",
  WhatsApp: "bg-green-50 text-green-700",
  Meeting: "bg-cyan-50 text-cyan-700",
  "Stage Change": "bg-gray-100 text-gray-500",
  "Lead Created": "bg-green-100 text-green-500",
  created: "bg-green-100 text-green-500",
  updated: "bg-blue-100 text-blue-500",
  Other: "bg-gray-100 text-gray-500",
};

const agentInitials = (name: string) =>
  name
    ? name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "S";

export function ActivityTimeline({ activities, onLogActivity }: Props) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 md:p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-sm font-semibold text-gray-900">Activity Timeline</h3>
        {onLogActivity && (
          <button
            onClick={onLogActivity}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-600 text-indigo-600 text-xs font-semibold hover:bg-indigo-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line text-sm" /> Log Activity
          </button>
        )}
      </div>

      <div className="relative pl-8">
        <div className="absolute left-[11px] top-1 bottom-1 w-0.5 bg-gray-200" />

        {activities.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-4">No activities logged yet.</p>
        ) : (
          <div className="space-y-5">
            {activities.map((activity) => {
              const iconColor = extendedIconColors[activity.type] || "bg-gray-100 text-gray-500";
              const icon = extendedIcons[activity.type] || "ri-more-line";
              const badgeColor = typeBadgeColors[activity.type] || "bg-gray-100 text-gray-500";

              return (
                <div key={activity.id} className="relative">
                  <div
                    className={`absolute -left-8 top-0.5 w-[22px] h-[22px] rounded-full flex items-center justify-center flex-shrink-0 ${
                      activity.systemEvent ? "bg-gray-100" : iconColor.split(" ")[0]
                    }`}
                  >
                    <i
                      className={`${icon} text-xs ${
                        activity.systemEvent ? "text-gray-400" : iconColor.split(" ")[1]
                      }`}
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badgeColor}`}>
                        {activity.type}
                      </span>
                      <span className="text-xs text-gray-400">{activity.timestamp}</span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                      {activity.description}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      {!activity.systemEvent && (
                        <div className="w-4 h-4 rounded-full bg-indigo-100 flex items-center justify-center text-[8px] font-bold text-indigo-700">
                          {agentInitials(activity.agent)}
                        </div>
                      )}
                      {activity.systemEvent && activity.agent !== "System" && (
                        <div className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center text-[8px] font-bold text-gray-600">
                          {agentInitials(activity.agent)}
                        </div>
                      )}
                      <span className="text-xs text-gray-500">
                        {activity.systemEvent && activity.agent !== "System"
                          ? `by ${activity.agent}`
                          : activity.systemEvent
                          ? "System"
                          : `by ${activity.agent}`}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}